import { useQueryClient, useMutation } from "@tanstack/react-query";
import api from "@/lib/api-client";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { formatDateTime } from "@/utils/format/date-time";

const statusTabs = {
    0: "upcoming",
    1: "upcoming",
    2: "upcoming",
    3: "declined-cancelled",
    4: "declined-cancelled",
};

const updateAppointment = async ({ appointmentGuid, data, statusId }) => {
    const payload = {
        StatusID: statusId,
        ...(data?.date &&
            data?.time && {
                StartDate: formatDateTime(data.date + " " + data.time),
            }),
        ...(data?.confirmContact && {
            ConfirmContact: data.confirmContact ? 1 : 0,
        }),
        ...(data?.confirmationType && {
            ConfirmationType: parseInt(data.confirmationType),
        }),
        ...(data?.notes !== undefined && {
            Notes: data.notes,
        }),
    };

    const res = await api.patch(
        `/Patient/Appointments/${appointmentGuid}`,
        payload,
    );

    return res.data;
};

export const useUpdateAppointment = (options) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: updateAppointment,
        ...options,
        retry: 1,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({
                queryKey: ["appointment"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["appointments"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["appointment-availability"],
            });

            toast.success("Appointment updated");

            const tab = statusTabs[variables.statusId];

            navigate(
                `/patient/appointments?tab=${tab}&id=${data.appointmentGuid}`,
            );

            await options?.onSuccess?.(data, variables, context);
        },
        onError: async (error, variables, context) => {
            toast.error("Error updating appointment");

            await options?.onError?.(error, variables, context);
        },
    });
};

const createAppointment = async ({ data, clinician, service }) => {
    const payload = {
        ServiceGuid: service.serviceGuid,
        StartDate: formatDateTime(data.date + " " + data.time),
        ConfirmContact: data.confirmContact ? 1 : 0,
        ConfirmationType: parseInt(data.confirmationType),
        ClinicianID: clinician.userID,
    };

    const res = await api.post("/Patient/Appointments", payload);

    return res.data;
};

export const useCreateAppointment = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: createAppointment,
        retry: 1,
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({
                queryKey: ["appointments"],
            });
            await queryClient.invalidateQueries({
                queryKey: ["appointment-availability"],
            });

            toast.success("Appointment successfully booked");

            navigate(
                `/patient/appointments?tab=upcoming&id=${data.appointmentGuid}`,
            );
        },
        onError: () => {
            toast.error("Error booking appointment");
        },
    });
};
